import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';
import {
	CFIND_5565_CREATION_FOCUSED_NAV_XFLOW,
	CREATION_FOCUSED_NAV_START_TIME,
	SIMPLIFY_NAV_NEW_USER,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	type useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import { getXflowExperimentStartDate } from '../getXflowExperimentStartDate.ts';
import { isWithinD0Window } from '../isWithinD0Window.ts';

const FIRST_ACTIVATION_DATE_TRAIT = 'firstActivationDate';

// Use a module-level variable to ensure the effect only fires once per app lifecycle,
// even if the hook is called from multiple places simultaneously.
let hasRunEffect = false;

// Test-only utility function to reset the hasRunEffect flag
export function resetHasRunEffect() {
	hasRunEffect = false;
}

type OnboardingStateResult = ReturnType<typeof useGetOnboardingState>;

type useIsEligibleForSimplifyNavXflowProps = {
	onboardingStateData: OnboardingStateResult['data'];
	onboardingStateLoading: OnboardingStateResult['loading'];
	onboardingStateError: OnboardingStateResult['error'];
	skip: boolean;
};

type useIsEligibleForSimplifyNavXflowResult = {
	isEligible: boolean;
	isLoading: boolean;
	hasError: boolean;
	isWithinD0Window: boolean;
};

export const useIsEligibleForSimplifyNavXflow = ({
	onboardingStateData,
	onboardingStateLoading,
	onboardingStateError,
	skip,
}: useIsEligibleForSimplifyNavXflowProps): useIsEligibleForSimplifyNavXflowResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { accessStatus } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	const {
		data: siteTraitsData,
		loading: siteTraitsDataLoading,
		error: siteTraitsDataError,
	} = useSiteTraits({ skipBootstrap: skip });

	const { setOnboardingState } = useSetOnboardingState();

	const entries = onboardingStateData?.onboardingState;

	const isInCfindLandCreationFocusedNavExperiment = !!entries?.find(
		(s) => s.key === CREATION_FOCUSED_NAV_START_TIME,
	)?.value;

	const isInActivationExperiment =
		entries?.find(({ key }) => key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_ELIGIBLE)
			?.value === 'true';

	const isOnboardingStateForExperimentSet =
		entries?.find(({ key }) => key === CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.CHECKED_ELIGIBLE)
			?.value === 'true';

	const xflowStartAt = entries?.find(
		({ key }) => key === CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.START_AT,
	)?.value;

	// We checked the user's eligibility at one point and determined they were not in treatment.
	const isReturningControlUser = isOnboardingStateForExperimentSet && !xflowStartAt;

	// A user who already has a START_AT recorded is a returning enrolled user and is always eligible.
	const isReturningEnrolledUser = !!xflowStartAt;

	const siteActivationDateTrait = siteTraitsData?.attributes?.find(
		({ name }) => name === FIRST_ACTIVATION_DATE_TRAIT,
	)?.value;

	const isLoading = !!(onboardingStateLoading || siteTraitsDataLoading);
	const hasError = !!(onboardingStateError || siteTraitsDataError);

	// A new user is eligible if their site's firstActivationDate is non-null and
	// after the experiment start date. Returning enrolled users are always eligible.
	const isSiteActivationAfterExperimentStart = (): boolean => {
		if (!siteActivationDateTrait) {
			return false;
		}
		const experimentStartDate = getXflowExperimentStartDate();
		if (!experimentStartDate) {
			return false;
		}
		try {
			return new Date(siteActivationDateTrait) >= experimentStartDate;
		} catch {
			return false;
		}
	};

	const isEligible =
		!skip &&
		!isLoading &&
		!hasError &&
		hasLicensedAccess &&
		!isInCfindLandCreationFocusedNavExperiment &&
		!isInActivationExperiment &&
		!isReturningControlUser &&
		(isReturningEnrolledUser || isSiteActivationAfterExperimentStart());

	useEffect(() => {
		if (!isEligible || isOnboardingStateForExperimentSet || hasRunEffect) {
			return;
		}
		hasRunEffect = true;

		if (expValEquals('cfind_5565_creation_focused_nav_xflow', 'cohort', 'pin-templates')) {
			setOnboardingState([
				{
					key: CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.CHECKED_ELIGIBLE,
					value: 'true',
				},
				{
					key: CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.START_AT,
					value: new Date().toISOString(),
				},
			]);
		} else {
			setOnboardingState({
				key: CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.CHECKED_ELIGIBLE,
				value: 'true',
			});
		}

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'eligible',
				actionSubject: 'cfind-5565-simplify-nav-xflow',
				actionSubjectId: 'cfind-5565-simplify-nav-xflow',
				source: 'cfind-5565-simplify-nav-xflow',
				attributes: {
					siteActivationDate: siteActivationDateTrait,
				},
			},
		}).fire();
	}, [
		isEligible,
		isOnboardingStateForExperimentSet,
		setOnboardingState,
		siteActivationDateTrait,
		createAnalyticsEvent,
	]);

	return {
		isLoading,
		hasError,
		isEligible,
		// For returning enrolled users, use their START_AT timestamp to determine if they're
		// within the D0 window. For first-time users (no START_AT yet), they're implicitly
		// within their D0 window if and only if they're eligible.
		isWithinD0Window: xflowStartAt ? isWithinD0Window(xflowStartAt) : isEligible,
	};
};
